import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { clientService, Client } from '../services/clientService';
import { strategyService, Strategy } from '../services/strategyService';
import { ArrowLeftIcon, TrashIcon } from '../components/Icons';
import { api } from '../services/api';

const getModuleName = (modStr: string | number) => {
  const str = String(modStr);
  if (str === 'Módulo 1' || str === '1') return 'Módulo I: El Problema';
  if (str === 'Módulo 2' || str === '2') return 'Módulo II: El Insight';
  if (str === 'Módulo 3' || str === '3') return 'Módulo III: Dirección Creativa';
  if (str === 'Módulo 4' || str === '4') return 'Módulo IV: La Verdad Activadora';
  if (str === 'Módulo 5' || str === '5') return 'Módulo V: Estrategia de Integración';
  if (str === 'Módulo 6' || str === '6') return 'Módulo VI: Accionables Creativos';
  if (str === 'Módulo 10' || str === '10') return 'Módulo X: Bifurcación';
  if (str === 'Módulo 11' || str === '11') return 'Módulo Y: Subida de Información';
  if (str === 'Módulo 12' || str === '12') return 'Módulo Z: Diagnóstico';
  if (str === 'Módulo 13' || str === '13') return 'Módulo 6-B: Accionables Integrados';
  if (str === 'Módulo 0' || str === '0') return 'Onboarding';
  return str;
};

interface ClientStrategy {
  id: string;
  name: string;
  module: string;
  status: string;
  progress: number;
  created_at: string;
  description: string;
}

const moduleNames: Record<number, string> = {
  1: 'Módulo 1: El Problema',
  2: 'Módulo 2: El Insight',
  3: 'Módulo 3: Dirección Creativa',
  4: 'Módulo 4: Verdad Activadora',
  5: 'Módulo 5: Integración',
  6: 'Módulo 6: Accionables',
  7: 'Completado',
  8: 'Completado',
  9: 'Completado',
  10: 'Módulo X: Bifurcación',
  11: 'Módulo Y: Subida Datos',
  12: 'Módulo Z: Diagnóstico',
  13: 'Módulo 6-B: Accionables Integrados',
};

const parseMaybeJSON = (value: any): any => {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
};

function mapStrategy(s: Strategy): ClientStrategy {
  let progress = 0;
  if (s.status === 'completed' || s.current_module === 7) {
    progress = 100;
  } else if (s.current_module === 13) {
    progress = 98;
  } else if (s.current_module === 12) {
    progress = 95;
  } else if (s.current_module === 11) {
    progress = 90;
  } else if (s.current_module === 10) {
    progress = 85;
  } else if (s.current_module === 6) {
    progress = 95;
  } else {
    progress = Math.min(Math.round((s.current_module / 6) * 100), 83);
  }

  // La estrategia solo está finalizada cuando el usuario aprobó el último
  // módulo (current_module = 7). El status "Estrategia Finalizada" también
  // se asigna al generar, antes de la aprobación, por eso no se usa solo.
  const isCompleted = s.status === 'completed' || s.current_module === 7;
  let name = s.project_name || 'Estrategia';
  let description = '';

  const m1 = parseMaybeJSON(s.module_1_problem);
  if (m1) {
    description = m1.parrafo || m1.opportunity || '';
  }

  const m2 = parseMaybeJSON(s.module_2_insight);
  if (m2 && !description) {
    description = m2.insight || m2.tension || '';
  }

  return {
    id: s.id,
    name: name,
    module: moduleNames[s.current_module] || moduleNames[1],
    status: isCompleted ? 'Finalizado' : 'En progreso',
    progress,
    created_at: new Date(s.created_at).toLocaleDateString('es-ES'),
    description: description || 'Estrategia en desarrollo',
  };
}

const statusLabels: Record<string, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  in_progress: 'En progreso',
};

const getStatusStyle = (status: string) => {
  switch (status) {
    case 'active': return { bg: 'var(--accent-light)', color: 'var(--accent-text)' };
    case 'in_progress': return { bg: 'rgba(217, 119, 6, 0.1)', color: 'var(--warning)' };
    case 'inactive': return { bg: 'rgba(220, 38, 38, 0.08)', color: 'var(--error)' };
    default: return { bg: 'var(--bg-hover)', color: 'var(--text-secondary)' };
  }
};

const getModuleColor = (module: string) => {
  if (module.includes('Módulo 1')) return 'var(--error)';
  if (module.includes('Módulo 2')) return 'var(--warning)';
  if (module.includes('Módulo 3')) return '#6366f1';
  if (module.includes('Módulo 4')) return 'var(--success)';
  if (module.includes('Módulo 5')) return '#8b5cf6';
  if (module.includes('Módulo 6')) return '#06b6d4';
  if (module.includes('Módulo X')) return '#ec4899';
  if (module.includes('Módulo Y')) return '#f97316';
  if (module.includes('Módulo Z')) return '#3b82f6';
  if (module.includes('Módulo 6-B')) return '#10b981';
  return 'var(--text-secondary)';
};

const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [client, setClient] = useState<Client | null>(null);
  const [clientStrategies, setClientStrategies] = useState<ClientStrategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteStratDialog, setDeleteStratDialog] = useState({ open: false, id: '', name: '' });
  const [activeTab, setActiveTab] = useState<'info' | 'strategies'>(
    searchParams.get('tab') === 'strategies' ? 'strategies' : 'info'
  );

  const fetchClientDetails = async () => {
    if (!id) return;
    try {
      const data = await clientService.getById(id);
      setClient(data);
      try {
        const strategies = await strategyService.list(data.id);
        setClientStrategies(strategies.map(mapStrategy));
      } catch (e) {
        setClientStrategies([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar cliente');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientDetails();
  }, [id]);

  const confirmDeleteStrategy = async () => {
    try {
      await strategyService.delete(deleteStratDialog.id);
      setDeleteStratDialog({ open: false, id: '', name: '' });
      fetchClientDetails();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error al eliminar la estrategia');
    }
  };

  const handleExport = async (strategyId: string) => {
    try {
      const res = await api.get(`/strategies/${strategyId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Estrategia_JJAgency_${strategyId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando PDF:', err);
      alert('Error al exportar el PDF. Asegúrate de estar autenticado.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>Cargando...</div>
      </Layout>
    );
  }

  if (error || !client) {
    return (
      <Layout>
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <div style={{ color: 'var(--error)', marginBottom: '1rem' }}>{error || 'Cliente no encontrado'}</div>
          <button onClick={() => navigate('/clients')} style={styles.backBtn}>← Volver a clientes</button>
        </div>
      </Layout>
    );
  }

  const statusStyle = getStatusStyle(client.status);

  return (
    <Layout>
      <div>
        <div style={styles.header}>
          <h1 style={styles.clientName}>{client.name}</h1>
        </div>

        <div style={styles.tabs}>
          <button
            onClick={() => setActiveTab('info')}
            style={{ ...styles.tab, ...(activeTab === 'info' ? styles.tabActive : {}) }}
          >
            Información General
          </button>
          <button
            onClick={() => setActiveTab('strategies')}
            style={{ ...styles.tab, ...(activeTab === 'strategies' ? styles.tabActive : {}) }}
          >
            Estrategias
          </button>
        </div>

        {activeTab === 'info' && (
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>Datos del Cliente</h2>
            <div style={styles.infoGrid}>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>Nombre</div>
                <div style={styles.infoValue}>{client.name}</div>
              </div>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>Industria</div>
                <div style={styles.infoValue}>{client.industry}</div>
              </div>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>Contacto</div>
                <div style={styles.infoValue}>{client.contact_name}</div>
              </div>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>Email</div>
                <div style={styles.infoValue}>{client.contact_email}</div>
              </div>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>Estado</div>
                <div style={styles.infoValue}>
                  <span style={{ ...styles.statusBadgeSmall, background: statusStyle.bg, color: statusStyle.color }}>
                    {statusLabels[client.status] || client.status}
                  </span>
                </div>
              </div>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>Estrategias Activas</div>
                <div style={styles.infoValue}>{client.strategies_count}</div>
              </div>
              <div style={styles.infoItem}>
                <div style={styles.infoLabel}>Fecha de Registro</div>
                <div style={styles.infoValue}>{new Date(client.created_at).toLocaleDateString('es-ES')}</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'strategies' && (
          <div>
            {clientStrategies.length === 0 ? (
              <div style={styles.card}>
                <div style={styles.emptyState}>
                  <button style={styles.addStratBtn} onClick={() => navigate(`/strategies/new/workflow?client_id=${client.id}&client_name=${encodeURIComponent(client.name)}`)}>+ Crear primera estrategia</button>
                </div>
              </div>
            ) : (
              <div style={styles.strategiesList}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                  <button style={styles.addStratBtn} onClick={() => navigate(`/strategies/new/workflow?client_id=${client.id}&client_name=${encodeURIComponent(client.name)}`)}>+ Nueva Estrategia</button>
                </div>
                {clientStrategies.map((s, index) => (
                  <div key={s.id} style={styles.strategyCard}>
                    <div style={styles.stratHeader}>
                      <div style={styles.stratNumber}>#{index + 1}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{
                          ...styles.stratStatusBadge,
                          background: s.status === 'Finalizado' ? 'var(--accent-light)' : 'rgba(217, 119, 6, 0.1)',
                          color: s.status === 'Finalizado' ? 'var(--accent-text)' : 'var(--warning)',
                        }}>{s.status}</div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteStratDialog({ open: true, id: s.id, name: s.name });
                          }}
                          style={styles.iconBtnDanger}
                          title="Eliminar estrategia"
                        >
                          <TrashIcon size={16} />
                        </button>
                      </div>
                    </div>

                    <h3 style={styles.stratName}>{s.name}</h3>
                    <p style={styles.stratDesc}>{s.description}</p>

                    <div style={styles.stratMeta}>
                      <div style={styles.stratMetaItem}>
                        <span style={styles.metaLabel}>Módulo</span>
                        <span style={{ ...styles.metaValue, color: getModuleColor(s.module) }}>{getModuleName(s.module)}</span>
                      </div>
                      <div style={styles.stratMetaItem}>
                        <span style={styles.metaLabel}>Creada</span>
                        <span style={styles.metaValue}>{s.created_at}</span>
                      </div>
                    </div>

                    <div style={styles.progressSection}>
                      <div style={styles.progressHeader}>
                        <span style={styles.progressLabel}>Progreso</span>
                        <span style={styles.progressPercent}>{s.progress}%</span>
                      </div>
                      <div style={styles.progressBarBg}>
                        <div style={{
                          ...styles.progressBarFill,
                          width: `${s.progress}%`,
                          background: s.status === 'Finalizado'
                            ? 'var(--success)'
                            : 'var(--accent)',
                        }} />
                      </div>
                    </div>

                    <div style={styles.stratActions}>
                      <button style={styles.stratActionBtnPrimary} onClick={() => navigate(`/strategies/${s.id}/workflow`)}>Continuar →</button>
                      <button style={styles.stratActionBtnSecondary} onClick={() => handleExport(s.id)}>Exportar</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <button onClick={() => navigate('/clients')} style={styles.backLink}>
          <ArrowLeftIcon size={16} color="var(--text-secondary)" />
          <span>Volver al listado</span>
        </button>

        {deleteStratDialog.open && (
          <div style={styles.dialogOverlay}>
            <div style={styles.dialogCard}>
              <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>Eliminar Estrategia</h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                ¿Estás seguro de que deseas eliminar la estrategia <strong>{deleteStratDialog.name}</strong>? Esta acción no se puede deshacer.
              </p>
              <div style={styles.dialogActions}>
                <button 
                  style={styles.btnCancel} 
                  onClick={() => setDeleteStratDialog({ open: false, id: '', name: '' })}
                >
                  Cancelar
                </button>
                <button 
                  style={styles.btnDanger} 
                  onClick={confirmDeleteStrategy}
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  header: { marginBottom: '1.5rem' },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
    padding: '0.7rem 1.5rem',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
    transition: 'all 0.2s',
    marginTop: '1.5rem',
    fontFamily: 'var(--font-body)',
  },
  backBtn: {
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    padding: '0.7rem 1.5rem',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
    fontFamily: 'var(--font-body)',
  },
  clientName: {
    fontSize: '2rem',
    fontWeight: 700,
    color: 'var(--text-primary)',
    marginBottom: '0.75rem',
  },
  statusBadgeSmall: {
    padding: '0.25rem 0.6rem',
    borderRadius: '20px',
    fontSize: '0.7rem',
    fontWeight: 700,
    display: 'inline-block',
  },
  tabs: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    borderBottom: '2px solid var(--border)',
  },
  tab: {
    padding: '0.85rem 1.5rem',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-muted)',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    borderBottom: '3px solid transparent',
    marginBottom: '-2px',
    transition: 'all 0.2s',
    fontFamily: 'var(--font-body)',
  },
  tabActive: {
    color: 'var(--accent)',
    borderBottomColor: 'var(--accent)',
  },
  card: {
    background: 'var(--bg-card)',
    borderRadius: 'var(--radius)',
    padding: '1.5rem',
    boxShadow: 'var(--shadow)',
    border: '1px solid var(--border)',
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: 'var(--text-primary)',
    marginBottom: '1.25rem',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '1.25rem',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  infoLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  infoValue: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  strategiesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
  },
  strategyCard: {
    background: 'var(--bg-card)',
    borderRadius: 'var(--radius-lg)',
    padding: '2rem',
    boxShadow: 'var(--shadow)',
    border: '1px solid var(--border)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  stratHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  stratNumber: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: 'var(--text-muted)',
    background: 'var(--bg-hover)',
    padding: '0.3rem 0.7rem',
    borderRadius: '6px',
  },
  stratStatusBadge: {
    fontSize: '0.8rem',
    fontWeight: 700,
    padding: '0.35rem 0.9rem',
    borderRadius: '20px',
  },
  iconBtnDanger: {
    background: 'none',
    border: 'none',
    color: 'var(--error)',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'background 0.2s',
  },
  stratName: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: 'var(--text-primary)',
    marginBottom: '0.5rem',
  },
  stratDesc: {
    fontSize: '0.95rem',
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
    marginBottom: '1.25rem',
  },
  stratMeta: {
    display: 'flex',
    gap: '2rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
  },
  stratMetaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2rem',
  },
  metaLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  metaValue: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  progressSection: {
    marginBottom: '1.5rem',
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  progressLabel: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
  },
  progressPercent: {
    fontSize: '1.1rem',
    fontWeight: 800,
    color: 'var(--text-primary)',
  },
  progressBarBg: {
    height: '10px',
    background: 'var(--bg-hover)',
    borderRadius: '5px',
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: '5px',
    transition: 'width 0.8s ease',
  },
  stratActions: {
    display: 'flex',
    gap: '0.75rem',
  },
  stratActionBtnPrimary: {
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    padding: '0.7rem 1.5rem',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
    flex: 1,
    fontFamily: 'var(--font-body)',
    transition: 'background 0.2s',
  },
  stratActionBtnSecondary: {
    background: 'var(--bg-hover)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
    padding: '0.7rem 1.5rem',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
    fontFamily: 'var(--font-body)',
    transition: 'all 0.2s',
  },
  emptyState: {
    padding: '4rem',
    textAlign: 'center',
    color: 'var(--text-muted)',
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  emptyText: {
    fontSize: '1.1rem',
    marginBottom: '1.5rem',
  },
  addStratBtn: {
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    padding: '0.7rem 1.5rem',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
    fontFamily: 'var(--font-body)',
  },
  dialogOverlay: {
    position: 'fixed' as const,
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  dialogCard: {
    backgroundColor: 'var(--bg-card)',
    padding: '2rem',
    borderRadius: '12px',
    width: '400px',
    maxWidth: '90%',
    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
    border: '1px solid var(--border)',
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    marginTop: '2rem',
  },
  btnCancel: {
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    fontWeight: '500',
  },
  btnDanger: {
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    border: 'none',
    background: 'var(--danger, #ef4444)',
    color: 'white',
    cursor: 'pointer',
    fontWeight: '500',
  },
};

export default ClientDetail;
